import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Switch, Divider, Chip,
  TableBody, TableRow, TableCell,
  TablePagination, CircularProgress, Alert,
} from '@mui/material';
import { WhatsApp as WhatsAppIcon } from '@mui/icons-material';
import whatsappService from '../services/whatsappService';
import useSnackbar from '../hooks/useSnackbar';
import PageLayout from '../components/ui/PageLayout';
import StandardCard from '../components/ui/StandardCard';
import StandardTable from '../components/ui/StandardTable';
import AppSnackbar from '../components/ui/AppSnackbar';
import EmptyState from '../components/ui/EmptyState';

const DIRECTION_LABEL = { outbound: 'Enviada', inbound: 'Recebida' };
const PURPOSE_LABEL = {
  confirmation_request: 'Confirmação',
  reminder: 'Lembrete',
  cancelled: 'Cancelamento',
  reply: 'Resposta',
};

const STATUS_COLOR = {
  sent:        'info',
  delivered:   'success',
  read:        'success',
  failed:      'error',
  undelivered: 'error',
  received:    'default',
};

const messageColumns = [
  { id: 'createdAt',   label: 'Data/Hora' },
  { id: 'direction',   label: 'Direção' },
  { id: 'purpose',     label: 'Tipo' },
  { id: 'status',      label: 'Status', align: 'center' },
  { id: 'phoneMasked', label: 'Telefone' },
];

function formatDateTime(isoStr) {
  if (!isoStr) return '—';
  return new Date(isoStr).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function IntegrationsPage() {
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  // ── Settings state ──────────────────────────────────────────────────────────
  const [enabled, setEnabled]               = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [togglingSettings, setTogglingSettings] = useState(false);

  // ── Messages state ──────────────────────────────────────────────────────────
  const [messages, setMessages]   = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(0);          // 0-indexed (TablePagination)
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState(null);

  // ── Load settings on mount ──────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const data = await whatsappService.getSettings();
        setEnabled(data.enabled ?? false);
      } catch {
        // Silencioso — default false
      } finally {
        setLoadingSettings(false);
      }
    })();
  }, []);

  // ── Load messages when page/rowsPerPage change ──────────────────────────────
  const loadMessages = useCallback(async (p, rpp) => {
    setLoadingMessages(true);
    setMessagesError(null);
    try {
      const data = await whatsappService.listMessages(p + 1, rpp); // API é 1-indexed
      setMessages(data.items ?? []);
      setTotal(data.total ?? 0);
    } catch (err) {
      setMessagesError('Não foi possível carregar o histórico. Tente novamente.');
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    loadMessages(page, rowsPerPage);
  }, [page, rowsPerPage, loadMessages]);

  // ── Toggle handler ──────────────────────────────────────────────────────────
  const handleToggle = useCallback(async (e) => {
    const newValue = e.target.checked;
    setTogglingSettings(true);
    try {
      await whatsappService.updateSettings(newValue);
      setEnabled(newValue);
      showSuccess(newValue ? 'WhatsApp habilitado com sucesso.' : 'WhatsApp desabilitado.');
    } catch {
      showError('Não foi possível atualizar a configuração. Tente novamente.');
      // Revert
    } finally {
      setTogglingSettings(false);
    }
  }, [showSuccess, showError]);

  // ── Pagination handlers ─────────────────────────────────────────────────────
  const handlePageChange = (_, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <PageLayout
      title="Integrações"
      subtitle="Gerencie suas integrações externas"
      icon={<WhatsAppIcon sx={{ color: '#25D366' }} />}
    >
      {/* ── Card WhatsApp ─────────────────────────────────────────────────── */}
      <StandardCard sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <WhatsAppIcon sx={{ color: '#25D366', fontSize: 28 }} />
          <Typography variant="h6">WhatsApp</Typography>
          {loadingSettings ? (
            <CircularProgress size={16} sx={{ ml: 'auto' }} />
          ) : (
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {enabled ? 'Habilitado' : 'Desabilitado'}
              </Typography>
              <Switch
                checked={enabled}
                onChange={handleToggle}
                disabled={togglingSettings}
                color="success"
              />
            </Box>
          )}
        </Box>

        <Typography variant="body2" color="text.secondary">
          Quando habilitado, seus clientes receberão mensagens de confirmação e lembretes de agendamento pelo WhatsApp.
          Você pode desabilitar a qualquer momento para parar os envios.
        </Typography>
      </StandardCard>

      {/* ── Histórico ────────────────────────────────────────────────────── */}
      <StandardCard>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Histórico de Mensagens
        </Typography>

        <Divider sx={{ mb: 2 }} />

        {messagesError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMessagesError(null)}>
            {messagesError}
          </Alert>
        )}

        {!loadingMessages && total === 0 && !messagesError ? (
          <EmptyState
            icon={WhatsAppIcon}
            title="Nenhuma mensagem enviada"
            message="Quando você habilitar o WhatsApp, o histórico de mensagens aparecerá aqui."
          />
        ) : (
          <>
            <StandardTable
              columns={messageColumns}
              loading={loadingMessages}
              isEmpty={messages.length === 0 && !loadingMessages}
              emptyMessage="Nenhuma mensagem nesta página."
            >
              <TableBody>
                {messages.map((msg, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{formatDateTime(msg.createdAt)}</TableCell>
                    <TableCell>
                      {DIRECTION_LABEL[msg.direction] ?? msg.direction}
                    </TableCell>
                    <TableCell>
                      {PURPOSE_LABEL[msg.purpose] ?? msg.purpose ?? '—'}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={msg.status}
                        color={STATUS_COLOR[msg.status] ?? 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>
                      {msg.phoneMasked}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </StandardTable>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 20, 50]}
              labelRowsPerPage="Por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            />
          </>
        )}
      </StandardCard>

      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </PageLayout>
  );
}
